import React from "react";
import { ResponsiveTable } from "../ResponsiveTable";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import { GapResult, SmtpRecentResponse } from "../troubleshootingInterfaces";
import {
  Card,
  CardHeader,
  CELL_SX,
  EmptyCard,
  GapNotice,
  HEAD_CELL_SX,
  Hint,
  KindChip,
  Mono,
  Pill,
  SampleChip,
  StatusDot,
  TS_COLORS,
  TsButton,
  SiteLink,
} from "../TroubleshootingUi";
import {
  fetchSiteCameraNames,
  fetchSmtpInstructions,
  fetchSmtpRecent,
} from "../TroubleshootingUtils/troubleshootingApi";
import { SmtpCameraConfiguration } from "./SmtpCameraConfiguration";
import { FilenameRuleSummary } from "./SmtpFilenameDistribution";
import {
  formatBytes,
  formatRelative,
} from "../TroubleshootingUtils/formatters";
import { useScopedSites } from "../TroubleshootingUtils/useScope";

// ---------------------------------------------------------------------------
// 1. Test email
// ---------------------------------------------------------------------------

/**
 * Whose side the fix is on when a test email did not arrive. Our receiver's
 * own state decides it: if it is not accepting mail, nothing the customer
 * changes will help.
 */
export const NotReceivedVerdict = ({
  receiverAccepting,
  host,
  port,
}: {
  receiverAccepting: boolean;
  host?: string;
  port?: string | number;
}) =>
  receiverAccepting ? (
    <Typography
      sx={{ ...MULISH, fontSize: 14, lineHeight: 1.5 }}
      data-testid="smtp-verdict-customer"
    >
      <Pill tone="orange">Fix is on your side</Pill> The connection is not
      leaving your network. Allow the NVR&rsquo;s IP outbound to{" "}
      <Mono>{host}</Mono> on port {port}, then check the NVR&rsquo;s own email
      log says &ldquo;sent&rdquo;. If it says &ldquo;authentication
      failed&rdquo;, set username and password to actuate / actuate.
    </Typography>
  ) : (
    <Typography
      sx={{ ...MULISH, fontSize: 14, lineHeight: 1.5 }}
      data-testid="smtp-verdict-actuate"
    >
      <Pill tone="info">Fix is on Actuate&rsquo;s side</Pill> Our email receiver
      is not accepting mail right now, so your test could not arrive. Nothing
      needs changing on your NVR. Copy the result for support and send it to us.
    </Typography>
  );

const SmtpTestEmailCard = ({
  siteId,
  siteName,
  onCopy,
}: {
  siteId: number;
  siteName: string;
  onCopy: (text: string) => void;
}) => {
  const { smtpInstructions, sampleData, siteDetailLoading } =
    useTroubleshootingStore();
  const setSmtpInstructions = useTroubleshootingStore(
    (s) => s.setSmtpInstructions
  );
  const [retrying, setRetrying] = React.useState(false);
  const retry = () => {
    setRetrying(true);
    fetchSmtpInstructions(siteId)
      .catch(() => null)
      .then((instructions) => {
        // Only this site's answer: the user may have moved on meanwhile.
        if (useTroubleshootingStore.getState().siteId === siteId)
          setSmtpInstructions(instructions);
        setRetrying(false);
      });
  };
  const [state, setState] = React.useState<"idle" | "listening" | "done">(
    "idle"
  );
  const [result, setResult] =
    React.useState<GapResult<SmtpRecentResponse> | null>(null);
  const [sampleMode, setSampleMode] = React.useState<"received" | "missing">(
    "received"
  );
  const sinceRef = React.useRef<string>("");
  const timerRef = React.useRef<number | null>(null);
  const sender = smtpInstructions?.recipient || "";

  React.useEffect(
    () => () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    },
    []
  );

  const listen = () => {
    sinceRef.current = new Date().toISOString();
    setState("listening");
    setResult(null);
    const startedAt = Date.now();
    const poll = async () => {
      const response = await fetchSmtpRecent(
        siteId,
        sinceRef.current,
        sender,
        sampleData,
        sampleMode
      );
      if (response.status !== "ok") {
        setResult(response);
        setState("done");
        if (timerRef.current) window.clearInterval(timerRef.current);
        return;
      }
      const arrived = response.data.emails.length > 0;
      // Two minutes is the window: most NVRs deliver within ten seconds of the test button.
      const timedOut = Date.now() - startedAt > (sampleData ? 2500 : 120_000);
      if (arrived || timedOut) {
        setResult(response);
        setState("done");
        if (timerRef.current) window.clearInterval(timerRef.current);
      }
    };
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(poll, sampleData ? 1200 : 5000);
    void poll();
  };

  const received =
    result?.status === "ok" && result.data.emails.length > 0
      ? result.data.emails[0]
      : null;

  return (
    <Card accent={TS_COLORS.smtp}>
      <CardHeader
        leading={<KindChip kind="smtp" />}
        title="SMTP test email"
        hint="Send a test from your NVR or camera, then confirm here that it reached Actuate."
        right={<Hint>{siteName}</Hint>}
      />
      <Box
        sx={{
          p: 2.5,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0,1fr) minmax(0,1.4fr)",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Hint
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 800,
            }}
          >
            Configure the device with
          </Hint>
          {smtpInstructions ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  border: `1px solid ${TS_COLORS.line}`,
                  borderRadius: "4px",
                  p: 1.5,
                }}
              >
                <Hint
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 800,
                    mb: 0.5,
                  }}
                >
                  Server
                </Hint>
                <Mono>{smtpInstructions.server_address}</Mono>
              </Box>
              <Box
                sx={{
                  border: `1px solid ${TS_COLORS.line}`,
                  borderRadius: "4px",
                  p: 1.5,
                }}
              >
                <Hint
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 800,
                    mb: 0.5,
                  }}
                >
                  Port
                </Hint>
                <Mono>{smtpInstructions.server_port}</Mono> <Hint>no TLS</Hint>
              </Box>
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  border: `1px solid ${TS_COLORS.line}`,
                  borderRadius: "4px",
                  p: 1.5,
                }}
              >
                <Hint
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 800,
                    mb: 0.5,
                  }}
                >
                  Sender and recipient
                </Hint>
                <Mono>{smtpInstructions.recipient}</Mono>
                <Box sx={{ mt: 0.5 }}>
                  <Hint>
                    Same address in both fields. Subject <Mono>Actuate</Mono>.
                    Attachment on. Auth none, or actuate / actuate if required.
                  </Hint>
                </Box>
              </Box>
            </Box>
          ) : siteDetailLoading || retrying ? (
            <Hint>Loading the site&rsquo;s SMTP settings…</Hint>
          ) : (
            // Loading finished without settings: say so, instead of a
            // loading line that never ends.
            <Box
              data-testid="smtp-settings-failed"
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              <StatusDot
                tone="bad"
                label="We couldn't load this site's SMTP settings."
              />
              <TsButton
                variant="outline"
                size="sm"
                color={TS_COLORS.smtp}
                onClick={retry}
                sx={{ alignSelf: "flex-start" }}
              >
                Retry
              </TsButton>
            </Box>
          )}
          <TsButton
            color={TS_COLORS.smtp}
            onClick={listen}
            disabled={state === "listening" || !smtpInstructions}
            sx={{ alignSelf: "flex-start" }}
          >
            {state === "listening"
              ? "Listening for your test email…"
              : state === "done"
              ? "Listen again"
              : "I sent a test, check for it"}
          </TsButton>
          <Hint>
            We watch for anything from <Mono>{sender.slice(0, 8)}…</Mono> for 2
            minutes. Fire the NVR&rsquo;s &ldquo;test email&rdquo; button, or
            wave at a camera.
          </Hint>
        </Box>
        <Box>
          {state === "idle" && (
            <Box
              sx={{
                p: 3,
                border: `1px dashed ${TS_COLORS.line}`,
                borderRadius: "4px",
              }}
            >
              <Hint>Result appears here.</Hint>
            </Box>
          )}
          {state === "listening" && (
            <Box
              sx={{
                p: 3,
                border: `1px dashed #c9b8f2`,
                borderRadius: "4px",
                display: "flex",
                gap: 1.25,
                alignItems: "center",
              }}
            >
              <StatusDot
                tone="warn"
                label={
                  <>
                    Waiting on{" "}
                    <Mono>
                      {smtpInstructions?.server_address}:
                      {smtpInstructions?.server_port}
                    </Mono>
                    … nothing yet. Most devices deliver within 10 seconds of
                    pressing Test.
                  </>
                }
              />
            </Box>
          )}
          {state === "done" && result && result.status !== "ok" && (
            <GapNotice
              result={result}
              what="The test-email listener"
              kind="check"
            />
          )}
          {state === "done" && result?.status === "ok" && received && (
            <Box
              sx={{
                border: "1px solid #b7dfc0",
                backgroundColor: "#f6fbf7",
                borderRadius: "4px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Pill tone="ok">Received</Pill>
                <Typography sx={{ ...MULISH, fontWeight: 700 }}>
                  Your test email reached Actuate{" "}
                  {formatRelative(received.received_at)}.
                </Typography>
                {result.sample && <SampleChip />}
              </Box>
              <ResponsiveTable>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      From
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Mono>{received.from_address}</Mono>{" "}
                      {received.from_address === sender && (
                        <Pill tone="ok">matches this site</Pill>
                      )}
                    </TableCell>
                  </TableRow>
                  {received.sending_ip && (
                    <TableRow>
                      <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                        Sending IP
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        <Mono>{received.sending_ip}</Mono>
                        {received.tls === false
                          ? ", no TLS"
                          : received.tls
                          ? ", TLS"
                          : ""}
                      </TableCell>
                    </TableRow>
                  )}
                  {received.subject && (
                    <TableRow>
                      <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                        Subject
                      </TableCell>
                      <TableCell sx={CELL_SX}>{received.subject}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      Attachment
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {received.attachment_filename ? (
                        <>
                          <Mono>{received.attachment_filename}</Mono>
                          {received.attachment_bytes != null
                            ? `, ${formatBytes(received.attachment_bytes)}`
                            : ""}{" "}
                          <Pill tone="ok">supported</Pill>
                        </>
                      ) : (
                        <Pill tone="bad">none</Pill>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      Camera identified
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {received.camera_identified ? (
                        <StatusDot
                          tone="ok"
                          label={received.camera_identified}
                        />
                      ) : received.camera_identified == null ? (
                        <StatusDot tone="grey" label="Not reported" />
                      ) : (
                        <StatusDot
                          tone="bad"
                          label="UNIDENTIFIED, fix the filename rule below"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      Analysed
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {received.analysed ? (
                        <StatusDot
                          tone="ok"
                          label="Yes, site armed and inside schedule."
                        />
                      ) : received.analysed == null ? (
                        <StatusDot tone="grey" label="Not reported" />
                      ) : (
                        <StatusDot
                          tone="grey"
                          label="No. Site disarmed or outside its schedule."
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </ResponsiveTable>
            </Box>
          )}
          {state === "done" && result?.status === "ok" && !received && (
            <Box
              sx={{
                border: "1px solid #f0c9a3",
                backgroundColor: "#fffaf5",
                borderRadius: "4px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Pill tone="bad">Not received</Pill>
                <Typography sx={{ ...MULISH, fontWeight: 700 }}>
                  Nothing from this site reached Actuate in 2 minutes.
                </Typography>
                {result.sample && <SampleChip />}
              </Box>
              <ResponsiveTable>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      Any email from <Mono>{sender.slice(0, 12)}…</Mono>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StatusDot
                        tone="bad"
                        label={`None in 2 min. Last one ever: ${
                          result.data.last_email_from_site
                            ? formatRelative(result.data.last_email_from_site)
                            : "never"
                        }.`}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...CELL_SX, color: TS_COLORS.ink3 }}>
                      Our receiver
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StatusDot
                        tone={result.data.receiver_accepting ? "ok" : "bad"}
                        label={
                          result.data.receiver_accepting
                            ? `Accepting mail; ${
                                result.data.receiver_emails_10m?.toLocaleString() ??
                                "many"
                              } emails from other sites in the last 10 min.`
                            : "Not accepting mail. This is on Actuate's side."
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </ResponsiveTable>
              <NotReceivedVerdict
                receiverAccepting={result.data.receiver_accepting}
                host={smtpInstructions?.server_address}
                port={smtpInstructions?.server_port}
              />
            </Box>
          )}
          {state === "done" && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TsButton
                variant="quiet"
                size="sm"
                onClick={() =>
                  onCopy(
                    `SMTP test email check for ${siteName}: ${
                      received
                        ? `received ${received.received_at} from ${received.from_address}`
                        : "not received within 2 minutes"
                    }`
                  )
                }
              >
                Copy result for support
              </TsButton>
              {sampleData && (
                <TsButton
                  variant="ghost"
                  size="sm"
                  color={TS_COLORS.orange}
                  onClick={() => {
                    setSampleMode(
                      sampleMode === "received" ? "missing" : "received"
                    );
                    setState("idle");
                  }}
                >
                  Sample: show the{" "}
                  {sampleMode === "received" ? "not received" : "received"}{" "}
                  state
                </TsButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 2. SMTP camera configuration (migrated from the Site About "SMTP Settings" tab)
// ---------------------------------------------------------------------------

const SmtpConfigurationCard = ({
  siteId,
  siteName,
  onSummary,
}: {
  siteId: number;
  siteName: string;
  onSummary: (summary: FilenameRuleSummary | null) => void;
}) => {
  const { smtpInstructions, siteDetail, sampleData, refreshToken } =
    useTroubleshootingStore();
  const [cameraNames, setCameraNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    fetchSiteCameraNames(siteId)
      .then((names) => {
        if (!cancelled)
          setCameraNames(
            sampleData && names.length === 0
              ? ["ch01", "ch02", "ch03", "ch04"]
              : names
          );
      })
      .catch(() => {
        if (!cancelled)
          setCameraNames(sampleData ? ["ch01", "ch02", "ch03", "ch04"] : []);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, sampleData, refreshToken]);

  // Clear the Diagnosis finding when the card unmounts (site or tab change).
  React.useEffect(() => () => onSummary(null), [onSummary]);

  const savedRule = React.useMemo(
    () => ({
      read_filename: Boolean(siteDetail?.read_filename),
      read_filename_expression: siteDetail?.read_filename_expression || "",
    }),
    [siteDetail?.read_filename, siteDetail?.read_filename_expression]
  );

  if (!smtpInstructions) return null;

  return (
    <Card accent={TS_COLORS.smtp} testId="smtp-configuration-card">
      <SmtpCameraConfiguration
        instructions={smtpInstructions}
        siteId={siteId}
        siteName={siteName}
        cameraNames={cameraNames}
        savedRule={savedRule}
        sampleData={sampleData}
        onSummary={onSummary}
      />
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 3. Common problems
// ---------------------------------------------------------------------------

const CommonProblems = ({
  host,
  port,
}: {
  host: string;
  port: string | number;
}) => {
  const rows: [string, string, React.ReactNode][] = [
    [
      "NVR says “connection refused” or “server unreachable”",
      "Your network blocks outbound SMTP.",
      <>
        Allow the NVR&rsquo;s IP outbound to <Mono>{host}</Mono> on port {port}.
        No inbound rules or VPN needed. Use the test above to confirm.
      </>,
    ],
    [
      "NVR says “sent”, test above says not received",
      "Wrong server, wrong port, or TLS forced on.",
      <>
        Re-enter <Mono>{host}</Mono>:{port}, turn TLS/SSL off, retest.
      </>,
    ],
    [
      "Test email received, no alarms in Actuate",
      "Site is disarmed or outside its schedule, or the email action isn’t linked to motion on the NVR.",
      "Check Armed and the Schedules tab; then link the email action to the motion event on the NVR.",
    ],
    [
      "Images arrive, camera shows UNIDENTIFIED",
      "Filename rule doesn’t match, or the camera name in Actuate differs from what it reads.",
      "Fix the filename rule above. The table shows what each filename reads as.",
    ],
    [
      "Only one camera works",
      "Per-camera mode with the same address pasted into every camera.",
      "Each camera needs its own address from the site wizard, or switch the site to one-sender mode.",
    ],
    [
      "“Authentication failed” or “TLS handshake failed”",
      "NVR insists on auth or TLS.",
      "Use actuate / actuate for credentials and turn TLS off. We don’t verify either.",
    ],
    [
      "Email arrives with no image processed",
      "Attachment toggle off, or an unsupported file type.",
      "Turn on “include attachment”; send .jpg, .png or .mp4.",
    ],
  ];
  return (
    <Card>
      <CardHeader
        title="Common SMTP problems"
        hint="In the order we see them."
      />
      <ResponsiveTable>
        <TableHead>
          <TableRow>
            {["What you see", "Most likely cause", "Fix"].map((h) => (
              <TableCell key={h} sx={HEAD_CELL_SX}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([see, cause, fix]) => (
            <TableRow key={see}>
              <TableCell sx={CELL_SX}>{see}</TableCell>
              <TableCell sx={CELL_SX}>{cause}</TableCell>
              <TableCell sx={CELL_SX}>{fix}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ResponsiveTable>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export const SmtpCamerasTab = ({
  onCopy,
  onFilenameSummary,
}: {
  onCopy: (text: string) => void;
  onFilenameSummary: (summary: FilenameRuleSummary | null) => void;
}) => {
  const {
    scope,
    siteId,
    smtpSiteIds,
    smtpInstructions,
    setScope,
    setGroupId,
    setSiteId,
    siteDetailLoading,
    groupId,
    sites,
    sitesLoading,
  } = useTroubleshootingStore();
  const scopedSites = useScopedSites();
  // In group scope this is the group's SMTP sites; in "all" it is every one.
  const smtpSites = scopedSites.filter((s) => smtpSiteIds.includes(s.id));

  if (scope !== "site") {
    return (
      <Card accent={TS_COLORS.smtp}>
        <CardHeader
          leading={<KindChip kind="smtp" />}
          title="SMTP camera sites"
          hint="Pick a site to test delivery and the filename rule. These tools work one site at a time."
        />
        {smtpSites.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Hint>
              {/* Sites load per group, so with no group chosen the list is
                  empty because nothing is loaded yet, not because the account
                  has no SMTP sites (QA SM-01, ENG-2187). */}
              {groupId == null && sites.length === 0
                ? "Choose a group above to list its SMTP camera sites."
                : sitesLoading
                ? "Loading sites…"
                : "No SMTP camera sites here. Sites that connect over a VPN or a direct address are on the Connections tab."}
            </Hint>
          </Box>
        ) : (
          <ResponsiveTable>
            <TableHead>
              <TableRow>
                {["Site", ""].map((h) => (
                  <TableCell key={h} sx={HEAD_CELL_SX}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {smtpSites.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={CELL_SX}>
                    <SiteLink siteId={s.id}>{s.name}</SiteLink>
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, textAlign: "right" }}>
                    <TsButton
                      variant="outline"
                      size="sm"
                      color={TS_COLORS.smtp}
                      onClick={() => {
                        setGroupId(s.groupId);
                        setScope("site");
                        setSiteId(s.id);
                      }}
                    >
                      Troubleshoot
                    </TsButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )}
      </Card>
    );
  }

  const site = scopedSites[0];
  if (!site || siteId == null)
    return <EmptyCard>Pick a site in the scope bar above.</EmptyCard>;
  if (siteDetailLoading && !smtpInstructions)
    return <EmptyCard>Loading {site.name}…</EmptyCard>;
  if (!smtpInstructions && !smtpSiteIds.includes(site.id)) {
    return (
      <EmptyCard>
        {site.name} does not send images by email. Its connection is on the
        Connections tab.
      </EmptyCard>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <SmtpTestEmailCard
        siteId={site.id}
        siteName={site.name}
        onCopy={onCopy}
      />
      <SmtpConfigurationCard
        siteId={site.id}
        siteName={site.name}
        onSummary={onFilenameSummary}
      />
      <CommonProblems
        host={smtpInstructions?.server_address || "smtp.actuateui.net"}
        port={smtpInstructions?.server_port || 25}
      />
    </Box>
  );
};
